import React, { useMemo } from 'react';
import SEOManager from './SEOManager';
import { Product, NewsItem, Business, CustomPage, BioLink, SiteSettings, GuideCategory, NewsCategory, ProductCategory } from '../types';
import { getBasePath } from '../constants';

interface HelmetProps {
  pageParam: string;
  articleSlug: string | null;
  productSlug: string | null;
  categorySlug: string | null;
  customPageSlug: string | null;
  bioLinkSlug: string | null;
  storySlug: string | null;
  siteSlug: string | null;
  settings: SiteSettings;
  products: Product[];
  news: NewsItem[];
  businesses: Business[];
  customPages: CustomPage[];
  bioLinks: BioLink[];
  newsCategories: NewsCategory[];
  productCategories: ProductCategory[];
  guideCategories: GuideCategory[];
  overrideTitle?: string;
  overrideDescription?: string;
}

export const Helmet: React.FC<HelmetProps> = ({
  pageParam,
  articleSlug,
  productSlug,
  categorySlug,
  customPageSlug,
  bioLinkSlug,
  storySlug,
  siteSlug,
  settings,
  products,
  news,
  businesses,
  customPages,
  bioLinks,
  newsCategories,
  productCategories,
  guideCategories,
  overrideTitle,
  overrideDescription
}) => {
  const metaData = useMemo(() => {
    const siteTitle = settings.siteTitle || "Portal de Conteúdo";
    const siteDescription = settings.siteDescription || "Sua fonte de informação e tecnologia.";
    const baseInstallUrl = settings.installationUrl || (typeof window !== 'undefined' ? (window.location.origin + getBasePath()) : '');
    const cleanInstallUrl = baseInstallUrl.endsWith('/') ? baseInstallUrl.slice(0, -1) : baseInstallUrl;

    let title = siteTitle;
    let description = siteDescription;
    let url = cleanInstallUrl;
    let image = settings.ogImageUrl || "";
    let type: 'website' | 'article' | 'product' = 'website';
    let publishedTime: string | undefined;
    let modifiedTime: string | undefined;
    let author: string | undefined;
    let section: string | undefined;
    let faq: { question: string; answer: string }[] | undefined;
    let price: string | undefined;
    let currency: string | undefined;
    let breadcrumbs: { name: string; item: string }[] = [];

    // Main breadcrumb root
    breadcrumbs.push({ name: "Início", item: "/" });

    if (overrideTitle) {
      return {
        title: overrideTitle,
        description: overrideDescription || siteDescription,
        url,
        image,
        type,
        settings
      };
    }

    // 1. Bio Links (/bio/:slug)
    if (pageParam === 'bio' && bioLinkSlug) {
      const bio = bioLinks.find(b => b.slug === bioLinkSlug);
      title = bio ? `${bio.title} | Links Úteis` : `Links Úteis`;
      description = bio ? (bio.description || `Confira a página de links importantes de ${bio.title}.`) : siteDescription;
      url = `${cleanInstallUrl}/bio/${bioLinkSlug}`;
      if (bio?.imageUrl) image = bio.imageUrl;
      
      breadcrumbs.push({ name: "Links", item: "/bio" });
      if (bio) breadcrumbs.push({ name: bio.title, item: `/bio/${bioLinkSlug}` });
    }
    // 2. Public Micro-sites (/site/:slug)
    else if (pageParam === 'site' && siteSlug) {
      title = `${siteSlug.toUpperCase()} | Site`;
      description = `Acesse a página exclusiva de ${siteSlug}.`;
      url = `${cleanInstallUrl}/site/${siteSlug}`;
      
      breadcrumbs.push({ name: "Sites", item: "/site" });
      breadcrumbs.push({ name: siteSlug, item: `/site/${siteSlug}` });
    }
    // 3. Web Story Viewer (/story/:slug)
    else if (pageParam === 'story' && storySlug) {
      title = `Web Story | ${siteTitle}`;
      description = `Acompanhe essa história em formato visual e dinâmico.`;
      url = `${cleanInstallUrl}/story/${storySlug}`;
      
      breadcrumbs.push({ name: "Web Stories", item: "/webstories" });
    }
    // 4. Web Stories Index (/webstories)
    else if (pageParam === 'webstories') {
      title = `Web Stories | ${siteTitle}`;
      description = `Confira as novidades do portal em stories dinâmicos e rápidos.`;
      url = `${cleanInstallUrl}/webstories`;
      
      breadcrumbs.push({ name: "Web Stories", item: "/webstories" });
    }
    // 5. Directory (Guia Comercial)
    else if (pageParam === 'guia' || (pageParam === 'home' && settings.directoryAsHomePage)) {
      breadcrumbs.push({ name: "Guia de Empresas", item: "/guia" });

      if (articleSlug) {
        // Business detail page (guia/:category/:slug or guia/:slug)
        const bus = businesses.find(b => b.slug === articleSlug || b.id.toString() === articleSlug.toString());
        title = bus ? `${bus.name} | Guia Comercial` : `Empresa`;
        description = bus ? (bus.description?.substring(0, 160).replace(/<[^>]*>/g, '') || `Conheça mais sobre ${bus.name}`) : siteDescription;
        url = `${cleanInstallUrl}/guia/${categorySlug || 'geral'}/${articleSlug}`;
        if (bus?.imageUrl) image = bus.imageUrl;

        if (categorySlug) {
          const cat = guideCategories.find(c => c.slug === categorySlug);
          breadcrumbs.push({ name: cat ? cat.name : categorySlug, item: `/guia/${categorySlug}` });
        }
        if (bus) {
          breadcrumbs.push({ name: bus.name, item: `/guia/${categorySlug || 'geral'}/${articleSlug}` });
        }
      } else if (categorySlug) {
        // Business category page (guia/:category)
        const cat = guideCategories.find(c => c.slug === categorySlug || c.name === categorySlug);
        title = cat ? `${cat.name} em nosso Guia de Empresas` : `Guia de Empresas`;
        description = `Confira as melhores empresas e prestadores de serviços de ${cat ? cat.name : categorySlug} na região.`;
        url = `${cleanInstallUrl}/guia/${categorySlug}`;
        
        breadcrumbs.push({ name: cat ? cat.name : categorySlug, item: `/guia/${categorySlug}` });
      } else {
        // Main Guia index
        title = `Guia Comercial e Empresas | ${siteTitle}`;
        description = `Encontre prestadores de serviços com avaliações, informações de contato e localização em nosso guia corporativo local.`;
        url = `${cleanInstallUrl}/guia`;
      }
    }
    // 6. Custom pages (/p/:slug)
    else if (customPageSlug) {
      const page = customPages.find(p => p.slug === customPageSlug);
      title = page ? `${page.title} | ${siteTitle}` : `Página`;
      description = page ? (page.meta_description || page.description || `Leia mais sobre ${page.title}`) : siteDescription;
      url = `${cleanInstallUrl}/p/${customPageSlug}`;
      
      breadcrumbs.push({ name: page ? page.title : customPageSlug, item: `/p/${customPageSlug}` });
    }
    // 7. Products (/produtos or /produtos/:slug)
    else if (pageParam === 'produtos') {
      breadcrumbs.push({ name: "Produtos", item: "/produtos" });

      if (productSlug) {
        // Single product
        const prod = products.find(p => p.slug === productSlug || p.id.toString() === productSlug.toString());
        title = prod ? `${prod.title} | ${siteTitle}` : `Produto`;
        description = prod ? (prod.description?.substring(0, 160).replace(/<[^>]*>/g, '') || `Adquira ${prod.title} e saiba mais detalhes.`) : siteDescription;
        url = `${cleanInstallUrl}/produtos/${productSlug}`;
        if (prod?.imageUrl) image = prod.imageUrl;
        type = 'product';

        if (prod) {
          breadcrumbs.push({ name: prod.title, item: `/produtos/${productSlug}` });
        }
      } else {
        // Main products list
        title = `Nossos Produtos e Soluções | ${siteTitle}`;
        description = `Explore e contrate agora mesmo nossos excelentes sistemas, ferramentas online, softwares e serviços premium de tecnologia.`;
        url = `${cleanInstallUrl}/produtos`;
      }
    }
    // 8. Blog articles & Category pages
    else if (pageParam === 'blog' || articleSlug) {
      breadcrumbs.push({ name: "Blog", item: "/blog" });

      if (articleSlug) {
        // Single blog article (/artigo/:category/:slug or /artigo/:slug)
        const article = news.find(n => n.slug === articleSlug || n.id.toString() === articleSlug.toString());
        title = article ? `${article.title} | ${siteTitle}` : `Artigo`;
        description = article ? (article.excerpt || article.content?.substring(0, 160).replace(/<[^>]*>/g, '') || `Confira a matéria completa sobre ${article.title}`) : siteDescription;
        url = `${cleanInstallUrl}/${categorySlug || 'geral'}/${articleSlug}`;
        if (article?.imageUrl) image = article.imageUrl;
        type = 'article';
        
        if (article) {
          publishedTime = article.date;
          author = siteTitle;
          section = article.category;
        }

        if (categorySlug) {
          const cat = newsCategories.find(c => c.slug === categorySlug);
          breadcrumbs.push({ name: cat ? cat.name : categorySlug, item: `/categoria/${categorySlug}` });
        }
        if (article) {
          breadcrumbs.push({ name: article.title, item: `/${categorySlug || 'geral'}/${articleSlug}` });
        }
      } else if (categorySlug) {
        // Blog category page (/categoria/:category)
        const cat = newsCategories.find(c => c.slug === categorySlug || c.name === categorySlug);
        title = cat ? `${cat.name} | Blog | ${siteTitle}` : `Artigos`;
        description = `Veja todas as nossas publicações e informativos inteligentes sobre ${cat ? cat.name : categorySlug}.`;
        url = `${cleanInstallUrl}/categoria/${categorySlug}`;
        
        breadcrumbs.push({ name: cat ? cat.name : categorySlug, item: `/categoria/${categorySlug}` });
      } else {
        // Blog main page
        title = `Blog de Notícias e Novidades | ${siteTitle}`;
        description = `Confira nosso hub de conhecimento, atualidades, inovações e artigos educativos atualizados constantemente.`;
        url = `${cleanInstallUrl}/blog`;
      }
    }
    // 9. Static standard pages
    else if (pageParam === 'quem-somos') {
      title = `Quem Somos | ${siteTitle}`;
      description = `Conheça a história de nossa empresa, nossa robusta missão institucional, nossos profundos valores corporativos e compromissos.`;
      url = `${cleanInstallUrl}/quem-somos`;
      breadcrumbs.push({ name: "Quem Somos", item: "/quem-somos" });
    }
    else if (pageParam === 'beneficios') {
      title = `Vantagens e Benefícios | ${siteTitle}`;
      description = `Entenda por que anunciar no portal. Confira os planos, retorno garantido, engajamento e as super vantagens comerciais que oferecemos.`;
      url = `${cleanInstallUrl}/beneficios`;
      breadcrumbs.push({ name: "Benefícios", item: "/beneficios" });
    }
    else if (pageParam === 'contato') {
      title = `Fale Conosco e Suporte | ${siteTitle}`;
      description = `Fale com nossa equipe técnica de suporte ou setor comercial. Envie sugestões, dúvidas, críticas e faça excelentes negócios conosco.`;
      url = `${cleanInstallUrl}/contato`;
      breadcrumbs.push({ name: "Contato", item: "/contato" });
    }
    else if (pageParam === 'privacidade') {
      title = `${settings.privacyPageTitle || "Política de Privacidade"} | ${siteTitle}`;
      description = settings.privacyPageDescription || `Consulte nossos termos de privacidade do portal sobre tratamento seguro de dados de usuários.`;
      url = `${cleanInstallUrl}/privacidade`;
      breadcrumbs.push({ name: "Privacidade", item: "/privacidade" });
    }
    else if (pageParam === 'termos') {
      title = `${settings.termsPageTitle || "Termos de Uso"} | ${siteTitle}`;
      description = settings.termsPageDescription || `Leia com atenção e confira as regras e condições de contratação e navegação do nosso portal.`;
      url = `${cleanInstallUrl}/termos`;
      breadcrumbs.push({ name: "Termos", item: "/termos" });
    }
    // 10. Admin
    else if (pageParam === 'admin') {
      title = `Painel de Administração | ${siteTitle}`;
      description = `Gerenciador Administrativo Intuitivo do Canal de Notícias e Guia.`;
      url = `${cleanInstallUrl}/admin`;
    }

    return {
      title,
      description,
      url,
      image,
      type,
      publishedTime,
      modifiedTime,
      author,
      section,
      faq,
      price,
      currency,
      settings,
      breadcrumbs
    };
  }, [
    pageParam, articleSlug, productSlug, categorySlug, customPageSlug, bioLinkSlug, storySlug, siteSlug,
    settings, products, news, businesses, customPages, bioLinks, newsCategories, productCategories, guideCategories,
    overrideTitle, overrideDescription
  ]);

  return <SEOManager {...metaData} />;
};
